import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import PanelLayout from "layouts/PanelLayout";
import {
  TbSmartHome,
  TbClock,
  TbTarget,
  TbInfoCircle,
  TbChecklist,
} from "react-icons/tb";
import "styles/teacher.css";

const TestDetailPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const test = state?.test;
  const subject = state?.subject;

  if (!test) {
    return (
      <PanelLayout>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="subject-page"
          style={{ textAlign: "center", padding: "150px 20px" }}
        >
          <h2 style={{ color: "#94a3b8" }}>Test topilmadi</h2>
        </motion.div>
      </PanelLayout>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
    hover: {
      scale: 1.03,
      transition: { duration: 0.3 },
    },
  };

  const questionVariants = {
    hidden: { opacity: 0, x: -40 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.6,
      },
    }),
    hover: {
      x: 10,
      transition: { duration: 0.3 },
    },
  };

  return (
    <PanelLayout>
      <motion.div
        className="test-detail-modern"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="tests-header">
          <motion.div className="breadcrumb" variants={itemVariants}>
            <TbSmartHome
              size={22}
              color="#01CC0D"
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/teacher")}
            />
            <span className="divider">/</span>
            <span
              onClick={() => navigate(-1)}
              style={{ cursor: "pointer", color: "#64748b" }}
            >
              Testlar
            </span>
            <span className="divider">/</span>
            <span className="current">{test.title}</span>
          </motion.div>
        </div>

        <motion.div className="test-hero" layout>
          <motion.div className="test-hero-content">
            <motion.h1
              className="test-title"
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.7 }}
            >
              {test.title}
            </motion.h1>

            {test.description && (
              <motion.p
                className="test-description"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {test.description}
              </motion.p>
            )}

            <motion.div
              className="badge-group"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <span
                className={`badge ${test.type?.toLowerCase() || "standart"}`}
              >
                {test.type || "Standart"}
              </span>
              <span className={`badge ${test.status?.toLowerCase()}`}>
                {test.status === "Created"
                  ? "Yangi"
                  : test.status === "Confirmed"
                  ? "Tasdiqlangan"
                  : test.status}
              </span>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.div className="test-info-grid" variants={containerVariants}>
          <motion.div
            className="info-card"
            variants={cardVariants}
            whileHover="hover"
          >
            <TbClock size={28} color="#01CC0D" />
            <div>
              <p className="info-label">Vaqt</p>
              <p className="info-value">{test.AllowedMinutes} daqiqa</p>
            </div>
          </motion.div>

          <motion.div
            className="info-card"
            variants={cardVariants}
            whileHover="hover"
          >
            <TbTarget size={28} color="#01CC0D" />
            <div>
              <p className="info-label">Urinishlar</p>
              <p className="info-value">{test.AttemptCount} marta</p>
            </div>
          </motion.div>

          <motion.div
            className="info-card"
            variants={cardVariants}
            whileHover="hover"
          >
            <TbChecklist size={28} color="#01CC0D" />
            <div>
              <p className="info-label">Savollar soni</p>
              <p className="info-value">{test.questions?.length || 0} ta</p>
            </div>
          </motion.div>
        </motion.div>

        <motion.div className="questions-section" variants={itemVariants}>
          <motion.h2
            className="section-title"
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.7 }}
          >
            <TbChecklist size={15} />
            Savollar ({test.questions?.length || 0})
          </motion.h2>

          {test.questions?.length > 0 ? (
            <motion.div className="questions-grid">
              {test.questions.map((q, i) => (
                <motion.div
                  key={i}
                  className="question-card-modern"
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                  variants={questionVariants}
                >
                  <div className="question-header">
                    <motion.span
                      className="question-number-badge"
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      {i + 1}
                    </motion.span>
                    <p className="question-text">
                      {q.questionText || q.question}
                    </p>
                  </div>

                  {q.imageUrl && (
                    <motion.div
                      className="question-image-wrapper"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      <img
                        src={q.imageUrl}
                        alt="Savol rasmi"
                        className="question-image-modern"
                      />
                    </motion.div>
                  )}

                  <div className="options-grid">
                    {q.options?.map((opt, j) => (
                      <motion.div
                        key={j}
                        className={`option-card ${
                          j === q.correctOptionIndex ? "correct-option" : ""
                        }`}
                        whileHover={{ scale: 1.05, x: 5 }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: j * 0.05 }}
                      >
                        <span className="option-icon">
                          {j === q.correctOptionIndex ? "✓" : "○"}
                        </span>
                        <span className="option-text">{opt}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              className="empty-questions"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              <TbInfoCircle size={40} color="#cbd5e1" />
              <p>Hozircha savollar qo‘shilmagan</p>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </PanelLayout>
  );
};

export default TestDetailPage;
